import { useMemo, useState } from 'react';
import {
  normaliseHeader,
  parseQuantity,
  parseRate,
  parseTable,
  readXlsx,
} from '../../reports/engine';
import { auditService } from '../../services/auditService';
import { fmtQty } from '../Reports/ui';

// Columns of the client's stock file. EAN and book quantity are required.
const FIELDS = [
  { key: 'ean', label: 'EAN / barcode', required: true, aliases: ['ean', 'barcode', 'bar code', 'ean code', 'upc', 'gtin', 'ean no', 'ean number'] },
  { key: 'book_qty', label: 'Book quantity', required: true, aliases: ['book qty', 'qty', 'quantity', 'soh', 'stock', 'stock qty', 'closing stock', 'closing qty', 'system qty', 'book stock', 'on hand', 'available qty'] },
  { key: 'mrp', label: 'MRP', aliases: ['mrp', 'mrp price', 'selling price', 'sp', 'retail price'] },
  { key: 'cost', label: 'Cost price', aliases: ['cost', 'cost price', 'cp', 'purchase price', 'landed cost', 'unit cost'] },
  { key: 'article', label: 'Article', aliases: ['article', 'article code', 'article no', 'sku', 'item code', 'material'] },
  { key: 'description', label: 'Description', aliases: ['description', 'item description', 'product name', 'product', 'item name', 'name', 'desc'] },
  { key: 'category', label: 'Category', aliases: ['category', 'department', 'dept', 'division', 'class', 'group'] },
];

function suggestMapping(headers) {
  const mapping = {};
  FIELDS.forEach((field) => {
    const aliases = new Set(field.aliases);
    const index = headers.findIndex((header) => aliases.has(normaliseHeader(header)));
    mapping[field.key] = index >= 0 ? String(index) : '';
  });
  return mapping;
}

/** Reads .xlsx / .csv / .txt into sheets whose rows are arrays of cell text. */
async function readSheets(file) {
  if (/\.xls$/i.test(file.name)) {
    throw new Error('Old .xls files are not supported. Save the file as .xlsx or CSV and try again.');
  }
  if (/\.xlsx$/i.test(file.name)) {
    const sheets = await readXlsx(new Uint8Array(await file.arrayBuffer()));
    return sheets.map((sheet) => ({ name: sheet.name, rows: sheet.rows.map((row) => row.cells) }));
  }
  const { rows } = parseTable(await file.text());
  return [{ name: file.name, rows: rows.map((row) => row.cells) }];
}

/**
 * Lets a manager load the client's book stock file: pick sheet and header
 * row, map columns, preview, then upload in parts.
 */
function BookStockUploader({ auditId, onUploaded, onCancel }) {
  const [file, setFile] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [headerRow, setHeaderRow] = useState(0);
  const [mapping, setMapping] = useState({});
  const [reading, setReading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');

  const sheet = sheets[sheetIndex];
  const headers = useMemo(() => (sheet ? sheet.rows[headerRow] || [] : []), [sheet, headerRow]);

  const chooseFile = async (event) => {
    const picked = event.target.files?.[0];
    event.target.value = '';
    if (!picked) return;
    setError('');
    setReading(true);
    try {
      const read = (await readSheets(picked)).filter((s) => s.rows.length > 0);
      if (!read.length) throw new Error('The file has no rows.');
      // Header = first row that maps both required columns, else row 1.
      let firstSheet = 0;
      let firstHeader = 0;
      outer: for (let s = 0; s < read.length; s += 1) {
        for (let r = 0; r < Math.min(read[s].rows.length, 15); r += 1) {
          const guess = suggestMapping(read[s].rows[r]);
          if (guess.ean !== '' && guess.book_qty !== '') {
            firstSheet = s;
            firstHeader = r;
            break outer;
          }
        }
      }
      setFile(picked);
      setSheets(read);
      setSheetIndex(firstSheet);
      setHeaderRow(firstHeader);
      setMapping(suggestMapping(read[firstSheet].rows[firstHeader] || []));
    } catch (err) {
      setError(err.message);
    } finally {
      setReading(false);
    }
  };

  const parsed = useMemo(() => {
    if (!sheet || mapping.ean === '' || mapping.book_qty === '' || mapping.ean === undefined) {
      return null;
    }
    const col = (row, key) => (mapping[key] === '' ? '' : row[Number(mapping[key])] ?? '');
    const rows = [];
    let skipped = 0;
    let badQty = 0;
    for (const row of sheet.rows.slice(headerRow + 1)) {
      const ean = String(col(row, 'ean')).trim();
      if (!ean) {
        if (row.some((cell) => String(cell).trim() !== '')) skipped += 1;
        continue;
      }
      const qty = parseQuantity(col(row, 'book_qty'));
      if (!qty.ok) {
        badQty += 1;
        continue;
      }
      rows.push({
        ean,
        book_qty: qty.value,
        mrp: mapping.mrp === '' ? null : parseRate(col(row, 'mrp')),
        cost: mapping.cost === '' ? null : parseRate(col(row, 'cost')),
        article: String(col(row, 'article')).trim() || null,
        description: String(col(row, 'description')).trim() || null,
        category: String(col(row, 'category')).trim() || null,
      });
    }
    const unique = new Set(rows.map((r) => r.ean)).size;
    return { rows, skipped, badQty, duplicates: rows.length - unique };
  }, [sheet, headerRow, mapping]);

  const upload = async () => {
    if (!parsed?.rows.length) return;
    setError('');
    setProgress({ part: 0, parts: Math.ceil(parsed.rows.length / 3000) });
    try {
      await auditService.uploadBookStock(
        auditId,
        { rows: parsed.rows, filename: file.name },
        setProgress,
      );
      setProgress(null);
      onUploaded();
    } catch (err) {
      setProgress(null);
      setError(`Book stock not uploaded: ${err.message}`);
    }
  };

  return (
    <div className="compare-upload">
      <div className="compare-upload__row">
        <label className="compare-upload__file">
          {reading ? 'Reading file…' : file ? `File: ${file.name}` : 'Choose book stock file (.xlsx, .csv, .txt)…'}
          <input type="file" accept=".xlsx,.csv,.txt,.tsv" onChange={chooseFile} disabled={reading || Boolean(progress)} />
        </label>
        <button type="button" className="compare-button compare-button--ghost" onClick={onCancel} disabled={Boolean(progress)}>
          Cancel
        </button>
      </div>

      {error ? <p className="compare-error">{error}</p> : null}

      {sheet ? (
        <>
          <div className="compare-upload__grid">
            {sheets.length > 1 ? (
              <label>
                Sheet
                <select
                  value={sheetIndex}
                  onChange={(event) => {
                    const index = Number(event.target.value);
                    setSheetIndex(index);
                    setHeaderRow(0);
                    setMapping(suggestMapping(sheets[index].rows[0] || []));
                  }}
                >
                  {sheets.map((s, index) => (
                    <option key={s.name} value={index}>
                      {s.name} ({fmtQty(s.rows.length)} rows)
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label>
              Header row
              <select
                value={headerRow}
                onChange={(event) => {
                  const row = Number(event.target.value);
                  setHeaderRow(row);
                  setMapping(suggestMapping(sheet.rows[row] || []));
                }}
              >
                {sheet.rows.slice(0, 15).map((row, index) => (
                  <option key={index} value={index}>
                    Row {index + 1}: {row.slice(0, 4).map((c) => c || '—').join(' | ')}
                  </option>
                ))}
              </select>
            </label>
            {FIELDS.map((field) => (
              <label key={field.key}>
                {field.label}
                {field.required ? ' *' : ''}
                <select
                  value={mapping[field.key] ?? ''}
                  onChange={(event) => setMapping((prev) => ({ ...prev, [field.key]: event.target.value }))}
                >
                  <option value="">{field.required ? 'Choose…' : 'Not in this file'}</option>
                  {headers.map((header, index) => (
                    <option key={index} value={String(index)}>
                      {index + 1}: {header || '(blank)'}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>

          {parsed ? (
            <>
              <p className="compare-muted">
                {fmtQty(parsed.rows.length)} rows ready
                {parsed.duplicates ? ` · ${fmtQty(parsed.duplicates)} repeated EAN rows (their quantities are added together)` : ''}
                {parsed.badQty ? ` · ${fmtQty(parsed.badQty)} rows without a valid quantity skipped` : ''}
                {parsed.skipped ? ` · ${fmtQty(parsed.skipped)} rows without an EAN skipped` : ''}
                {mapping.mrp === '' ? ' · no MRP column: MRP values will be blank' : ''}
                {mapping.cost === '' ? ' · no cost column: cost values will be blank' : ''}
              </p>
              <div className="compare-table-scroll">
                <table className="compare-table">
                  <thead>
                    <tr>
                      <th>EAN</th>
                      <th>Book qty</th>
                      <th>MRP</th>
                      <th>Cost</th>
                      <th>Article</th>
                      <th>Description</th>
                      <th>Category</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.slice(0, 5).map((row, index) => (
                      <tr key={index}>
                        <td>{row.ean}</td>
                        <td className="num">{fmtQty(row.book_qty)}</td>
                        <td className="num">{row.mrp ?? '—'}</td>
                        <td className="num">{row.cost ?? '—'}</td>
                        <td>{row.article || '—'}</td>
                        <td>{row.description || '—'}</td>
                        <td>{row.category || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="compare-upload__row">
                <button
                  type="button"
                  className="compare-button"
                  onClick={upload}
                  disabled={!parsed.rows.length || Boolean(progress)}
                >
                  {progress
                    ? `Uploading part ${Math.min(progress.part + 1, progress.parts)} of ${progress.parts}…`
                    : `Upload ${fmtQty(parsed.rows.length)} rows as book stock`}
                </button>
                <span className="compare-muted">Replaces any book stock already loaded for this schedule.</span>
              </div>
            </>
          ) : (
            <p className="compare-muted">Choose the EAN and book quantity columns.</p>
          )}
        </>
      ) : null}
    </div>
  );
}

export default BookStockUploader;
